import { observer } from 'mobx-react-lite';
import { useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import CopyButton from '@/components/blocks/CopyButton';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/dialog';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { ScrollableTable } from '@/components/ui/ScrollableTable';
import { SearchInput } from '@/components/ui/SearchInput';
import {
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@/components/ui/Table';
import { Tooltip } from '@/components/ui/Tooltip';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/utils/analytics';
import { formatDateLocale } from '@/utils/date';

import { getInviteUrl, teamApis } from '../team.query';
import { teamStore } from '../team.store';
import {
  MemberStatusEnum,
  MemberTierEnum,
  TeamRoleEnum,
  TeamRoleDisplayLabel,
  TierDisplayLabel,
  type MemberStatus,
  type MemberTier,
  type MyTeam,
  type TeamMember,
} from '../team.types';

import { InviteMemberAction } from './InviteMemberAction';

interface MembersTableProps {
  team: MyTeam;
  members: TeamMember[];
  isAdmin: boolean;
  currentUserEmail: string | undefined;
  hasActiveSubscription: boolean;
}

export const MembersTable = observer(function MembersTable({
  team,
  members,
  isAdmin,
  currentUserEmail,
  hasActiveSubscription,
}: MembersTableProps) {
  const [search, setSearch] = useState('');

  const query = search.trim().toLowerCase();
  const filteredMembers = query
    ? members.filter(
        (m) =>
          m.email.toLowerCase().includes(query) ||
          (m.name ?? '').toLowerCase().includes(query)
      )
    : members;

  const columnCount = isAdmin ? 5 : 4;

  return (
    <YStack className="gap-3">
      <XStack className="items-center justify-between gap-3">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search members..."
          aria-label="Search members"
          className="w-64 flex-none"
        />
        <InviteMemberAction
          teamId={team.id}
          members={members}
          isAdmin={isAdmin}
        >
          {({ onClick }) => (
            <Button
              variant="default"
              size="sm"
              onClick={onClick}
              disabled={!hasActiveSubscription}
            >
              <Icons.plus className="w-3.5 h-3.5" />
              Invite
            </Button>
          )}
        </InviteMemberAction>
      </XStack>

      <ScrollableTable
        maxHeight="max-h-[calc(100dvh-16rem)]"
        header={
          <TableRow className="border-b border-border">
            <TableHead className="px-4">Member</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Tier</TableHead>
            <TableHead>Status</TableHead>
            {isAdmin && (
              <TableHead className="w-32 text-right pr-4">
                <span className="sr-only">Actions</span>
              </TableHead>
            )}
          </TableRow>
        }
      >
        <TableBody>
          {filteredMembers.map((member) => (
            <MemberRow
              key={member.id}
              member={member}
              teamId={team.id}
              isAdmin={isAdmin}
              currentUserEmail={currentUserEmail}
            />
          ))}

          {filteredMembers.length === 0 && (
            <TableRow>
              <TableCell
                colSpan={columnCount}
                className="py-8 text-center text-sm text-foreground-muted"
              >
                {members.length === 0
                  ? 'No members yet. Invite your first team member.'
                  : `No members match "${search}".`}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </ScrollableTable>
    </YStack>
  );
});

const MemberRow = observer(function MemberRow({
  member,
  teamId,
  isAdmin,
  currentUserEmail,
}: {
  member: TeamMember;
  teamId: string;
  isAdmin: boolean;
  currentUserEmail: string | undefined;
}) {
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [pendingTier, setPendingTier] = useState<string | null>(null);
  const isSelf = member.email === currentUserEmail;
  const loading = teamStore.mutatingMemberId === member.id;

  return (
    <TableRow
      className={cn(
        'bg-surface hover:bg-surface-hover',
        loading && 'opacity-60 pointer-events-none'
      )}
    >
      <TableCell className="px-4 py-3">
        <YStack className="gap-0.5 min-w-0">
          <MemberNameWithTooltip
            name={member.name || member.email}
            joinedAt={member.joined_at}
            isSelf={isSelf}
          />
          {member.name && (
            <span className="text-xs text-foreground-muted truncate">
              {member.email}
            </span>
          )}
        </YStack>
      </TableCell>

      <TableCell>
        {isAdmin && member.role !== TeamRoleEnum.enum.owner && !isSelf ? (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger size="sm" disabled={loading}>
              <span className="w-12 text-left">
                {TeamRoleDisplayLabel[member.role]}
              </span>
              <Icons.chevronDown className="size-3 shrink-0" />
            </DropdownMenu.Trigger>
            <DropdownMenu.Content
              align="start"
              side="bottom"
              className="w-[8rem]"
            >
              <DropdownMenu.Item
                onSelect={() =>
                  teamApis.updateMemberRole(
                    teamId,
                    member.id,
                    { role: TeamRoleEnum.enum.admin },
                    member.role
                  )
                }
              >
                Admin
              </DropdownMenu.Item>
              <DropdownMenu.Item
                onSelect={() =>
                  teamApis.updateMemberRole(
                    teamId,
                    member.id,
                    { role: TeamRoleEnum.enum.member },
                    member.role
                  )
                }
              >
                Member
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        ) : isAdmin && member.role === TeamRoleEnum.enum.owner ? (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger
              size="sm"
              disabled
              className="opacity-70 cursor-not-allowed"
            >
              <span className="w-12 text-left">
                {TeamRoleDisplayLabel[member.role]}
              </span>
              <Icons.chevronDown className="size-3 shrink-0" />
            </DropdownMenu.Trigger>
          </DropdownMenu.Root>
        ) : (
          <span className="text-sm text-foreground">
            {TeamRoleDisplayLabel[member.role]}
          </span>
        )}
      </TableCell>

      <TableCell>
        {isAdmin ? (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger size="sm" disabled={loading}>
              <span className="w-8 text-left">
                {TierDisplayLabel[member.tier]}
              </span>
              <Icons.chevronDown className="size-3 shrink-0" />
            </DropdownMenu.Trigger>
            <DropdownMenu.Content
              align="start"
              side="bottom"
              className="w-[8rem]"
            >
              <DropdownMenu.Item
                onSelect={() => {
                  if (member.tier !== MemberTierEnum.enum.plus)
                    setPendingTier(MemberTierEnum.enum.plus);
                }}
              >
                Plus
              </DropdownMenu.Item>
              <DropdownMenu.Item
                onSelect={() => {
                  if (member.tier !== MemberTierEnum.enum.power)
                    setPendingTier(MemberTierEnum.enum.power);
                }}
              >
                Power
              </DropdownMenu.Item>
              <DropdownMenu.Item
                onSelect={() => {
                  if (member.tier !== MemberTierEnum.enum.pro)
                    setPendingTier(MemberTierEnum.enum.pro);
                }}
              >
                Pro
              </DropdownMenu.Item>
              <DropdownMenu.Item
                onSelect={() => {
                  if (member.tier !== MemberTierEnum.enum.pro2)
                    setPendingTier(MemberTierEnum.enum.pro2);
                }}
              >
                Pro2
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        ) : (
          <span className="text-sm text-foreground">
            {TierDisplayLabel[member.tier]}
          </span>
        )}
      </TableCell>

      <TableCell>
        <StatusBadge status={member.status} />
      </TableCell>

      {isAdmin && (
        <TableCell className="w-32 pr-4">
          <XStack className="justify-end gap-1">
            {member.status === MemberStatusEnum.enum.invited &&
              member.invite_token && (
                <CopyButton
                  text={getInviteUrl(member.invite_token)}
                  copyText="Copy link"
                  size="xs"
                  onCopy={() =>
                    trackEvent('TEAM_INVITE_LINK_COPIED', {
                      source: 'member_table',
                    })
                  }
                />
              )}
            {member.role !== TeamRoleEnum.enum.owner && !isSelf && (
              <Button
                variant="ghost"
                size="xs"
                onClick={() => setShowRemoveConfirm(true)}
                disabled={loading}
                className="text-foreground-muted hover:text-danger-11"
              >
                {member.status === MemberStatusEnum.enum.invited
                  ? 'Revoke invite'
                  : 'Remove'}
              </Button>
            )}
          </XStack>
        </TableCell>
      )}

      <Dialog.Root open={showRemoveConfirm} onOpenChange={setShowRemoveConfirm}>
        <Dialog.Content className="max-w-sm">
          <Dialog.Header>
            <Dialog.Title>
              {member.status === MemberStatusEnum.enum.invited
                ? `Revoke invite to ${member.email}?`
                : `Remove ${member.name || member.email}`}
            </Dialog.Title>
            <Dialog.Description>
              {member.status === MemberStatusEnum.enum.invited
                ? 'The invite link will stop working and they will no longer be able to join the team.'
                : 'Are you sure you want to remove this member from the team? Their seat will be freed and billing will be adjusted.'}
            </Dialog.Description>
          </Dialog.Header>
          <Dialog.Footer>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowRemoveConfirm(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => {
                setShowRemoveConfirm(false);
                teamApis.removeMember(teamId, member.id);
              }}
            >
              {member.status === MemberStatusEnum.enum.invited
                ? 'Revoke invite'
                : 'Remove'}
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Root>

      <Dialog.Root
        open={pendingTier !== null}
        onOpenChange={(open) => {
          if (!open) setPendingTier(null);
        }}
      >
        <Dialog.Content className="max-w-sm">
          <Dialog.Header>
            <Dialog.Title>
              Change tier for {member.name || member.email}?
            </Dialog.Title>
            <Dialog.Description>
              This will change their tier from {TierDisplayLabel[member.tier]}{' '}
              to{' '}
              {pendingTier
                ? TierDisplayLabel[pendingTier as keyof typeof TierDisplayLabel]
                : ''}
              . Billing will be adjusted immediately.
            </Dialog.Description>
          </Dialog.Header>
          <Dialog.Footer>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setPendingTier(null)}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() => {
                if (pendingTier) {
                  teamApis.updateMemberTier(
                    teamId,
                    member.id,
                    { tier: pendingTier as MemberTier },
                    member.tier
                  );
                }
                setPendingTier(null);
              }}
            >
              Confirm
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Root>
    </TableRow>
  );
});

function MemberNameWithTooltip({
  name,
  joinedAt,
  isSelf,
}: {
  name: string;
  joinedAt: string | null;
  isSelf: boolean;
}) {
  const label = (
    <span className="text-sm font-medium text-foreground truncate">
      {name}
      {isSelf && (
        <span className="text-foreground-muted font-normal"> (you)</span>
      )}
    </span>
  );

  if (!joinedAt) return label;

  const joined = formatDateLocale(joinedAt, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });

  return <Tooltip label={`Joined ${joined}`}>{label}</Tooltip>;
}

const STATUS_STYLES: Record<
  MemberStatus,
  { label: string; className: string }
> = {
  active: { label: 'Active', className: 'bg-success-3 text-success-11' },
  invited: { label: 'Invited', className: 'bg-warning-3 text-warning-11' },
  deactivated: {
    label: 'Deactivated',
    className: 'bg-surface-secondary text-foreground-muted',
  },
};

function StatusBadge({ status }: { status: MemberStatus }) {
  const { label, className } = STATUS_STYLES[status];
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        className
      )}
    >
      {label}
    </span>
  );
}
